import React from 'react';
import { Day } from '../atoms/Day';
import { WeekDay } from '../atoms/WeekDay';
import { useCalendar } from '../hooks/useCalendar';
import { useDateRange } from '../hooks/useDateRange';
import { CalendarGridProps } from '../types/Calendar.types';

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentDate,
  selectedDate,
  selectedRange,
  onDateSelect,
  onDateHover,
  minDate,
  maxDate,
  weekDays = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
  startOfWeek = 1,
  showWeekNumbers = false,
  hoverDate,
  renderDay,
}) => {
  const { getCalendarDays, isToday } = useCalendar(
    currentDate,
    () => {},
    () => {}
  );
  const { getRangeState } = useDateRange(
    selectedRange || { start: null, end: null },
    () => {},
    minDate,
    maxDate
  );

  const isDateSelected = (date: Date) => {
    if (!selectedDate) return false;
    // Comparar día civil: las celdas son medianoche local; con showTime selectedDate incluye hora.
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const calendarDays = getCalendarDays(currentDate, startOfWeek);

  // Sincronizar header con startOfWeek: weekDays en orden natural [Dom(0), Lun(1), ..., Sab(6)].
  // Columna j muestra el día (startOfWeek + j) % 7 → tomamos weekDays[ese índice].
  const orderedWeekDays = Array.from({ length: 7 }, (_, j) => weekDays[(startOfWeek + j) % 7]);

  return (
    <div className="ui-mx-auto ui-grid ui-w-fit ui-grid-cols-7 ui-gap-y-0.5">
      {/* Días de la semana (ordenados según startOfWeek) */}
      {orderedWeekDays.map((day, index) => (
        <WeekDay key={index} day={day} />
      ))}

      {/* Días del calendario */}
      {calendarDays.map((day, index) => {
        if (day === null) {
          return <div key={index} className="ui-h-control-xs ui-w-control-xs" />;
        }

        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);

        // Obtener estado del rango si existe
        const rangeState = selectedRange
          ? getRangeState(date, hoverDate || null)
          : {
              isStart: false,
              isEnd: false,
              isInRange: false,
              isFirstInRange: false,
              isLastInRange: false,
              afterSelected: true,
              isDisabled: isDateDisabled(date),
              isHovered: false,
            };

        return (
          <Day
            key={index}
            day={day}
            date={date}
            isSelected={isDateSelected(date)}
            isInRange={rangeState.isInRange}
            isStart={rangeState.isStart}
            isEnd={rangeState.isEnd}
            isFirstInRange={rangeState.isFirstInRange}
            isLastInRange={rangeState.isLastInRange}
            afterSelected={rangeState.afterSelected}
            isDisabled={rangeState.isDisabled}
            isToday={isToday(date)}
            isHovered={rangeState.isHovered}
            onClick={onDateSelect}
            onMouseEnter={onDateHover}
            onMouseLeave={() => onDateHover?.(null)}
            renderDay={renderDay}
          />
        );
      })}
    </div>
  );
};
